import { useState } from 'react'
import { EVENTS, MAX_BOTS } from '@sh/shared'
import { request } from '../../net/socket.js'

/**
 * Seats bots at this table, so a game can be reached without five people.
 *
 * Only ever rendered on a deployment that said it was `dev` - see
 * net/deployment.ts - and refused by the server as well, so a tab left open
 * across a redeploy is harmless rather than confusing. Two locks, each of
 * which fails closed on its own.
 *
 * This is not a ClientAction and so does not go through `act`: seating bots
 * changes no game state and the engine knows nothing about it. HostTools does
 * the same thing for claim PINs, and for the same reason.
 *
 * Presets rather than a stepper. The two counts anyone wants on this screen
 * are "enough to start" and "a full table", and both are numbers the lobby
 * already knows - a stepper would make the host do the subtraction. The
 * buttons disable at their cap rather than disappearing: a dead "fill to 10"
 * at ten seats says the cap is why, where a missing one says nothing.
 */
export function DevBots({ count, maxBots }: { count: number; maxBots: number }) {
  const [error, setError] = useState<string | null>(null)

  /** Seats as many as asked for, or as many as fit - the server decides. */
  const add = async (wanted: number) => {
    const ack = await request<{ seated: number; names: string[] }>(EVENTS.addBots, {
      count: Math.max(1, Math.min(wanted, maxBots || MAX_BOTS)),
    })
    setError(ack.ok ? null : ack.error)
  }

  const toStart = Math.max(0, 5 - count)
  const toFull = Math.max(0, 10 - count)

  return (
    <section className="card card--dev" data-testid="dev-bots">
      <h2 className="card__title">
        Bots <span className="card__count">dev server only</span>
      </h2>
      <p className="card__hint">
        Not part of the game. This server was started with APP_ENV=dev; a live one has no such
        card, and refuses the action behind it. Bots sit down, vote and legislate on their own,
        and the ✕ above removes one like any other player.
      </p>
      <div className="choices choices--row" data-testid="dev-bot-choices">
        <button
          type="button"
          className="btn btn--ghost btn--small"
          disabled={toFull === 0}
          onClick={() => void add(1)}
          data-testid="dev-bots-one"
        >
          +1
        </button>
        <button
          type="button"
          className="btn btn--ghost btn--small"
          disabled={toStart === 0}
          onClick={() => void add(toStart)}
          data-testid="dev-bots-to-start"
        >
          fill to 5
        </button>
        <button
          type="button"
          className="btn btn--ghost btn--small"
          disabled={toFull === 0}
          onClick={() => void add(toFull)}
          data-testid="dev-bots-fill"
        >
          fill to 10
        </button>
      </div>
      {/*
        Shown here rather than through the page's error banner, because this
        card is not part of the game and its failures should not look like a
        refused move.
      */}
      {error && (
        <p className="card__hint card__hint--bad" role="alert" data-testid="dev-bots-error">
          {error}
        </p>
      )}
    </section>
  )
}
